import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useState } from "react";
import { useCart, buildWhatsAppUrl } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { SpiceNote } from "./SpiceNote";
import { CheckoutModal } from "./CheckoutModal";

export const CartSheet = () => {
    const { items, open, setOpen, remove, update, subtotal, weightGrams, clear } = useCart();
    const { lang } = useLang();
    const [checkoutOpen, setCheckoutOpen] = useState(false);


    const t = (en, te) => (lang === "en" ? en : te);

    const cartMessage = () => {
        const lines = items
            .map(
                (i, idx) =>
                    `${idx + 1}. ${i.name.en} (${i.name.te}) — ${i.weight} × ${i.qty}`,
            )
            .join("\n");
        return `Hello Kumari Pickles\n\nI'd like to order:\n${lines}\n\nPlease share delivery details & final total.`;
    };

    const openCheckout = () => {
        setCheckoutOpen(true);
    };

    return (
        <>
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setOpen(false)}
                            className="fixed inset-0 z-[70] bg-[color:var(--kp-ink)]/40 backdrop-blur-sm"
                        />
                        <motion.aside
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "tween", ease: [0.19, 1, 0.22, 1], duration: 0.6 }}
                            className="fixed top-0 right-0 h-full w-full sm:w-[440px] z-[80] bg-[color:var(--kp-cream)] flex flex-col border-l border-[color:var(--kp-ink)]/15"
                            data-testid="cart-sheet"
                        >
                            <div className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--kp-ink)]/12">
                                <div>
                                    <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
                                        Basket · {items.length} items
                                    </div>
                                    <h3 className="font-serif-display text-2xl tracking-editorial">
                                        {t("Your Basket", "మీ బాస్కెట్")}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="p-1 hover:text-[color:var(--kp-red)]"
                                    aria-label="Close cart"
                                    data-testid="cart-close"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                {items.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-16">
                                        <div className="font-serif-display italic text-3xl text-[color:var(--kp-ink)]/50">
                                            Empty basket.
                                        </div>
                                        <p className="text-sm text-[color:var(--kp-ink)]/60 max-w-xs">
                                            {t(
                                                "Nothing here yet. Pick a pickle and fill your basket.",
                                                "మీ బాస్కెట్ ఖాళీగా ఉంది. ఒక ఊరగాయ ఎంచుకోండి.",
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-[color:var(--kp-ink)]/12">
                                        {items.map((it) => (
                                            <li key={it.key} className="py-4 flex gap-4" data-testid={`cart-item-${it.key}`}>
                                                <div className="w-20 h-24 shrink-0 overflow-hidden clipped-frame">
                                                    <img
                                                        src={it.image}
                                                        alt={it.name.en}
                                                        className="absolute inset-0 w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col justify-between">
                                                    <div>
                                                        <div className="font-display font-bold text-lg leading-tight">
                                                            {lang === "en"
                                                                ? `${it.name.en} (${it.name.te})`
                                                                : `${it.name.te} (${it.name.en})`}
                                                        </div>
                                                        <div className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 mt-0.5">
                                                            {it.weight} · ₹{it.price}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-2">
                                                        <div className="flex items-center gap-2 border border-[color:var(--kp-ink)]/20 rounded-full px-2 py-1">
                                                            <button
                                                                onClick={() => update(it.key, it.qty - 1)}
                                                                className="p-1"
                                                                data-testid={`decrement-${it.key}`}
                                                                aria-label="Decrease quantity"
                                                            >
                                                                <Minus size={12} />
                                                            </button>
                                                            <span className="text-sm w-5 text-center" data-testid={`qty-${it.key}`}>{it.qty}</span>
                                                            <button
                                                                onClick={() => update(it.key, it.qty + 1)}
                                                                className="p-1"
                                                                data-testid={`increment-${it.key}`}
                                                                aria-label="Increase quantity"
                                                            >
                                                                <Plus size={12} />
                                                            </button>
                                                        </div>
                                                        <button
                                                            onClick={() => remove(it.key)}
                                                            className="text-[color:var(--kp-ink)]/50 hover:text-[color:var(--kp-red)] transition-colors"
                                                            data-testid={`remove-${it.key}`}
                                                            aria-label="Remove item"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {items.length > 0 && (
                                <div className="border-t border-[color:var(--kp-ink)]/12 px-6 py-5 space-y-3 bg-[color:var(--kp-bone)]/40">
                                    <div className="flex items-baseline justify-between text-sm" data-testid="cart-shipping-note">
                                        <span className="text-[color:var(--kp-ink)]/60">Shipping</span>
                                        <span className="text-[11px] text-[color:var(--kp-ink)]/60 text-right max-w-[60%]">
                                            {weightGrams >= 1000
                                                ? "Free in AP & Telangana · else by pincode"
                                                : "Calculated at checkout · free on 1 kg+ in AP/TS"}
                                        </span>
                                    </div>
                                    <div className="flex items-baseline justify-between pt-2 border-t border-[color:var(--kp-ink)]/10">
                                        <span className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
                                            SUBTOTAL
                                        </span>
                                        <span
                                            className="font-serif-display text-3xl"
                                            data-testid="cart-subtotal"
                                        >
                                            ₹{subtotal}
                                        </span>
                                    </div>
                                    <button
                                        onClick={openCheckout}
                                        className="w-full inline-flex items-center justify-between bg-[color:var(--kp-red)] text-[color:var(--kp-cream)] px-5 py-4 rounded-full text-[13px] tracking-mono-wide hover:bg-[color:var(--kp-red-deep)] transition-colors"
                                        data-testid="cart-checkout"
                                    >
                                        <span>{t("Checkout", "చెల్లింపు")}</span>
                                        <ArrowRight size={14} />
                                    </button>
                                    <a
                                        href={buildWhatsAppUrl(cartMessage())}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-center border border-[color:var(--kp-ink)]/20 px-5 py-3 rounded-full text-[12px] tracking-mono-wide hover:border-[color:var(--kp-ink)]/50 transition-colors"
                                        data-testid="cart-quick-whatsapp"
                                    >
                                        {t("Quick order via WhatsApp", "వాట్సాప్‌లో త్వరిత ఆర్డర్")}
                                    </a>
                                    <button
                                        onClick={clear}
                                        className="w-full text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 hover:text-[color:var(--kp-red)] transition-colors"
                                        data-testid="cart-clear"
                                    >
                                        Clear basket
                                    </button>
                                    <SpiceNote testId="cart-spice-custom-note" />
                                </div>
                            )}
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
            <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
        </>
    );
};
